/**
 * Teaching Period terminology helpers for schedule exports.
 *
 * The product uses institution-neutral, portable terminology: the concept
 * formerly labelled "Semester" is presented as "Teaching Period" in exports.
 * @module utils/teaching-period
 */

/** What a teaching period is called where the programme names no label of its own. */
export const TEACHING_PERIOD_LABEL = "Teaching Period";

/** The programme's teaching period label, or the neutral one. */
export function programmeTeachingPeriodLabel(programme: { teachingPeriodLabel?: string }): string {
  const label = programme.teachingPeriodLabel?.trim();
  return label || TEACHING_PERIOD_LABEL;
}

/**
 * A label as a stage once recorded it, without its number: stages held
 * "Semester 1" as often as "Semester". Must match migration 0061's backfill.
 */
export function teachingPeriodLabelFrom(value: unknown): string | undefined {
  const label = typeof value === "string" ? value.trim().replace(/[\d ]+$/, "") : "";
  return label || undefined;
}

/**
 * A stage's teaching period options, numbered from 1 within the stage. A stage
 * of several also offers them all together, as the comma list `parseSemesters`
 * reads, for a module that runs the whole stage.
 */
export function stageTeachingPeriodOptions(
  count: number,
  label = TEACHING_PERIOD_LABEL,
): { value: string; label: string }[] {
  const periods = Array.from({ length: Math.max(0, count) }, (_, index) => String(index + 1));
  const single = periods.map((value) => ({ value, label: `${label} ${value}` }));
  return periods.length > 1
    ? [
        ...single,
        { value: periods.join(", "), label: `All teaching periods (${periods.join(", ")})` },
      ]
    : single;
}

const NUMBERED_PLACEMENT = /^\d+(?:\s*,\s*\d+)*$/;

/** A stored placement as it reads: "Semester 2", "Semester 1, 2", or free text as written. */
export function teachingPeriodName(
  value: string | undefined,
  label = TEACHING_PERIOD_LABEL,
): string {
  const text = value?.trim() ?? "";
  return NUMBERED_PLACEMENT.test(text) ? `${label} ${text}` : text;
}

/** Where a placement sits: "Year 2 · Semester 1", or the stage alone without a period. */
export function placementName(
  stageName: string,
  value: string | undefined,
  label = TEACHING_PERIOD_LABEL,
): string {
  const period = teachingPeriodName(value, label);
  return period ? `${stageName} · ${period}` : stageName;
}

/**
 * Normalises any "Semester" wording in a value to "Teaching Period" so that
 * schedule exports use portable, institution-neutral terminology.
 */
export function formatTeachingPeriod(value: string): string {
  return value.replace(/semester/gi, TEACHING_PERIOD_LABEL);
}

/**
 * Extracts the bare teaching-period value (e.g. "1") from a stored period
 * string (e.g. "Semester 1"), stripping any leading period-label word so it
 * can be paired with a separate "Teaching Period:" heading without repeating
 * the label (avoiding "Teaching Period Teaching Period 1").
 */
export function formatTeachingPeriodValue(value: string): string {
  return formatTeachingPeriod(value)
    .replace(new RegExp(`^${TEACHING_PERIOD_LABEL}\\s+`, "i"), "")
    .trim();
}
